import { Tab } from '@headlessui/react';
import parse from 'html-react-parser';
import linkifyHtml from 'linkify-html';

import { Asset } from '~modules/media-library/model/Asset';
import { DimensionTable } from '~modules/media-library/view/DetailModal/DimensionTable';

type PanelTabsProps = {
  asset: Asset;
};

export function PanelTabs({ asset }: PanelTabsProps) {
  if (!asset.tabs) {
    return (
      <div className="flex flex-col gap-2 pb-2">
        <div className="bg-gray rounded-full h-4 animate-pulse w-1/3" />
        <div className="bg-gray rounded-full h-4 animate-pulse w-2/3" />
        <div className="bg-gray rounded-full h-4 animate-pulse w-1/2" />
        <div className="bg-gray rounded-full h-4 animate-pulse w-1/3" />
      </div>
    );
  }

  return (
    <Tab.Group>
      <Tab.List className="border-t-0 border-l-0 border-r-0 border-b border-solid border-darkgray flex gap-6">
        {Object.keys(asset.tabs).map(tabKey => (
          <Tab
            key={tabKey}
            className={({ selected }) =>
              `appearance-none border-t-0 border-l-0 border-r-0 border-b-2 border-solid outline-none font-sans text-sm text-black bg-transparent m-0 px-0 pb-1 cursor-pointer ${
                selected ? 'border-black font-semibold' : 'border-transparent'
              }`
            }
          >
            {tabKey}
          </Tab>
        ))}
      </Tab.List>
      <Tab.Panels className="my-4">
        {Object.values(asset.tabs).map((tabContent, idx) => (
          <Tab.Panel key={idx} className="flex flex-col gap-2">
            {Object.entries(tabContent).map(([key, value], idx) => {
              if (key === 'recommended_dimensions') {
                return <DimensionTable asset={asset} key={`${key}_${idx}`} />;
              }
              if (key === 'blank_line') {
                return <p key={`${key}_${idx}`} />;
              }
              let attributeClassKey = key.toLowerCase().replaceAll(' ', '');
              return (
                <dl key={key} className="flex flex-wrap text-sm font-sans">
                  <dt className="w-1/3 pr-2 text-darkgray">{key}</dt>
                  <dd className={`w-2/3 ml-auto attribute-${attributeClassKey}`}>
                    {renderDescription(linkifyHtml(`${value}`))}
                  </dd>
                </dl>
              );
            })}
          </Tab.Panel>
        ))}
      </Tab.Panels>
    </Tab.Group>
  );
}

function renderDescription(text: string) {
  const html = (text || '')
    // ### Headline
    // -> <h3>Headline</h3>
    .replace(/^### (.*$)/gim, '<h3>$1</h3>')
    // ## Headline
    // -> <h2>Headline</h2>
    .replace(/^## (.*$)/gim, '<h2>$1</h2>')
    // # Headline
    // -> <h1>Headline</h1>
    .replace(/^# (.*$)/gim, '<h1>$1</h1>')
    // **bold text**
    // -> <b>bold text</b>
    .replace(/\*\*(.*)\*\*/gim, '<b>$1</b>')
    // *italic text*
    // -> <i>italic text</i>
    .replace(/\*(.*)\*/gim, '<i>$1</i>')
    // linebreaks (\n\r)
    // -> <br/>
    .replace(/\r\n|\r|\n/gim, '<br/>')
    // <https://www.example.com>
    // -> <a href="https:/www.example.com">https://www.example.com</a>
    .replace(
      /(?:<|&lt;)(https:\/\/|http:\/\/|mailto:)(.*?)(?:>|&gt;)/gim,
      '<a target="_blank" rel="noopener noreferrer" href="$1$2">$1$2</a>',
    )
    // [Linktext](http://www.example.com)
    // -> <a href="http://www.example.com">Linktext</a>
    .replace(/\[([^[]+)\](\(([^)]*))\)/gim, '<a target="_blank" rel="noopener noreferrer" href="$3">$1</a>')
    // [Linktext|http://www.example.com]
    // -> <a href="http://www.example.com">Linktext</a>
    .replace(
      /\[(.+?)\|(https:\/\/|http:\/\/|mailto:)(.*?)\]/gim,
      '<a target="_blank" rel="noopener noreferrer" href="$2$3">$1</a>',
    )
    // [http://www.example.com]
    // -> <a href="http://www.example.com">http://www.example.com</a>
    .replace(
      /\[(https:\/\/|http:\/\/|mailto:)(.*?)\]/gim,
      '<a target="_blank" rel="noopener noreferrer" href="$1$2">$1$2</a>',
    );

  return parse(html);
}
